import { useTranslation } from "next-i18next";
import { ReactNode } from "react";

import { Background } from "@baseComponents/Background";
import { BorderedImage } from "@baseComponents/BorderedImage";
import { ArticleContainer, ArticleContent } from "@baseComponents/css";
import { Header } from "@baseComponents/Header";
import { CTA } from "@components/CTA";
import Footer from "@components/footer";
import { Menu } from "@components/menu/Menu";
import { PageMetadata } from "@components/PageMetadata";
import { BlogPostPageProps } from "@lib/blog";

interface BlogPostLayoutProps extends BlogPostPageProps {
  children?: ReactNode;
}

function BlogPostLayoutBody({
  post: { title, description, author, date, headerImage, cta, ctaButtonText, faq },
  children,
}: BlogPostLayoutProps) {
  const { t } = useTranslation();

  return (
    <>
      <PageMetadata
        title={t(title)}
        description={t(description)}
        blogPostDate={date}
        faq={faq}
      />

      <Background withBlurBoxes />

      <Menu />

      <ArticleContainer maxWidth="desktopXl">
        <Header
          header={title}
          blogPostProps={{ author, date }}
          className="pb-4"
          variant="blogPost"
        />

        <div className="m-auto pb-6">
          {headerImage && (
            <BorderedImage
              src={headerImage.src}
              alt={headerImage.alt}
              priority
            />
          )}
        </div>

        <ArticleContent>{children}</ArticleContent>

        <CTA
          className="mt-8"
          buttonText={ctaButtonText ?? t("landing.cta.button")}
          header={cta ? t("landing.cta.header") : undefined}
          subheader={cta ?? t("landing.cta.subheader")}
        />
      </ArticleContainer>

      <Footer />
    </>
  );
}

export const BlogPostLayout = ({ children, ...rest }: BlogPostLayoutProps) => {
  return <BlogPostLayoutBody {...rest}>{children}</BlogPostLayoutBody>;
};
